/**
 * AdminCourseManager.jsx  — v2
 *
 * Kurs listesi: PageHeader + FilterBar + DataTable + ActionMenu
 * Kurs editor:  Dialog (shadcn) + 5 tab: Genel / Moduller / Dersler / Quiz / Sertifika
 * Moduller:     @dnd-kit drag-drop ile reorder (nested lesson drag-drop da var)
 * State:        React Query (useCourses / useCourseModules / mutations)
 * i18n:         t('admin.courseManager.*') — sifir hardcoded TR/EN string
 *
 * Eski AdminCourseManager.js dosyasi dokunulmadan saklanmistir (rollback).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Save, Trash2, Upload, Image, X, CheckCircle, Play, Zap, FileText,
  BookOpen, GripVertical, Edit2, Users, Plane, FileCheck,
  Mic, Volume2, Video as VideoIcon, ChevronDown, ChevronUp,
  Layers, Link2, AlignJustify, MessageSquare, Type, Repeat, Timer,
  Search, Folder, FolderOpen, Clock, ChevronLeft,
} from 'lucide-react';
import axios from 'axios';

const VIMEO_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lesson content editors (Faz 4e)
import {
  FlashcardEditor,
  MatchingEditor,
  DragDropEditor,
  DialogueEditor,
  FillInBlankEditor,
  SwipeEditor,
  TimeRaceEditor,
} from '../components/admin/lesson-editors';

// Admin layout & primitives
import AdminExamConfigModal from '../components/AdminExamConfigModal';
import AdminLayout from '../components/admin/AdminLayout';
import {
  PageHeader, FilterBar, DataTable, ActionMenu, ConfirmDialog,
  FormSection, FormField, EmptyState, StatusBadge,
} from '../components/admin';

// shadcn/ui
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';

// Context
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';

// API
import { apiClient } from '../services/apiClient';
import {
  useCourses, useCourseModules, useCreateCourse, useUpdateCourse, useDeleteCourse,
  useCreateModule, useUpdateModule, useDeleteModule,
  useReorderModules, useReorderLessons,
  useCreateLesson, useUpdateLesson, useDeleteLesson,
  useCourse,
} from '../api/queries/courses';

// dnd-kit
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'programming',  label_en: 'Programming',       label_tr: 'Programlama' },
  { id: 'design',       label_en: 'Design',             label_tr: 'Tasarım' },
  { id: 'business',     label_en: 'Business',           label_tr: 'İş Dünyası' },
  { id: 'marketing',    label_en: 'Marketing',          label_tr: 'Pazarlama' },
  { id: 'music',        label_en: 'Music',              label_tr: 'Müzik' },
  { id: 'photography',  label_en: 'Photography',        label_tr: 'Fotoğraf' },
  { id: 'language',     label_en: 'Languages',          label_tr: 'Diller' },
  { id: 'health',       label_en: 'Health',             label_tr: 'Sağlık' },
  { id: 'cabin_crew',   label_en: 'Cabin Crew',         label_tr: 'Kabin Memurluğu' },
  { id: 'hr_training',  label_en: 'HR Training',        label_tr: 'İnsan Kaynakları' },
];

const HR_SUBCATEGORIES = [
  { id: 'recruitment',          label_en: 'Recruitment',          label_tr: 'İşe Alım' },
  { id: 'interview_techniques', label_en: 'Interview Techniques', label_tr: 'Mülakat Teknikleri' },
  { id: 'hr_management',        label_en: 'HR Management',        label_tr: 'İK Yönetimi' },
  { id: 'performance',          label_en: 'Performance Mgmt',     label_tr: 'Performans' },
  { id: 'payroll',              label_en: 'Payroll & Records',    label_tr: 'Bordro' },
  { id: 'other',                label_en: 'Other',                label_tr: 'Diğer' },
];

const LESSON_TYPES = [
  // Legacy types
  { id: 'video',        label_en: 'Video',            label_tr: 'Video',              icon: Play },
  { id: 'interactive',  label_en: 'Interactive Quiz', label_tr: 'İnteraktif Quiz',    icon: Zap },
  { id: 'pdf',          label_en: 'PDF Document',     label_tr: 'PDF Doküman',        icon: FileText },
  { id: 'text',         label_en: 'Text Content',     label_tr: 'Metin İçerik',       icon: BookOpen },
  { id: 'voice',        label_en: 'Voice',            label_tr: 'Ses',                icon: Mic },
  { id: 'interview',    label_en: 'Interview',        label_tr: 'Mülakat',            icon: Volume2 },
  // Faz 4e — new interactive types
  { id: 'flashcard',    label_en: 'Flashcard',        label_tr: 'Kelime Kartı',       icon: Layers },
  { id: 'matching',     label_en: 'Matching',         label_tr: 'Eşleştirme',         icon: Link2 },
  { id: 'drag_drop',    label_en: 'Drag & Drop',      label_tr: 'Sürükleme',          icon: AlignJustify },
  { id: 'dialogue',     label_en: 'Dialogue',         label_tr: 'Diyalog',            icon: MessageSquare },
  { id: 'fill_in_blank',label_en: 'Fill in Blank',    label_tr: 'Boşlukları Doldur',  icon: Type },
  { id: 'swipe',        label_en: 'Swipe Vocabulary', label_tr: 'Kelime Kaydırma',    icon: Repeat },
  { id: 'time_race',    label_en: 'Time Race',        label_tr: 'Zaman Yarışı',       icon: Timer },
];

// Map of lesson_type -> editor component (Faz 4e)
const CONTENT_EDITORS = {
  flashcard:     FlashcardEditor,
  matching:      MatchingEditor,
  drag_drop:     DragDropEditor,
  dialogue:      DialogueEditor,
  fill_in_blank: FillInBlankEditor,
  swipe:         SwipeEditor,
  time_race:     TimeRaceEditor,
};

const LEVELS = ['beginner', 'intermediate', 'advanced'];
const LEVEL_REQUIRED_OPTIONS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CURRENCIES = ['TRY', 'USD', 'EUR'];
const LANGUAGES = ['tr', 'en'];

// ─── Varsayilan form degerleri ────────────────────────────────────────────────
const DEFAULT_COURSE_FORM = {
  title: '', title_tr: '', description: '', description_tr: '',
  category: 'programming', subcategory: '', price: 0, currency: 'TRY',
  thumbnail: '', vimeo_intro_url: '', level: 'beginner', language: 'tr',
  is_interactive: false, level_required: null,
  certificate_info: { title: '', subtitle: '' },
  sinav_giris_gun_sayisi: 0, sertifika_adi_tr: '', sertifika_adi_en: '',
};

const DEFAULT_MODULE_FORM = {
  title: '', title_tr: '', description: '', order: 1,
};

const DEFAULT_LESSON_FORM = {
  title: '', title_tr: '', title_en: '', description: '', description_tr: '',
  order: 1, lesson_type: 'video', module_id: '', module_title: '',
  vimeo_url: '', content: '', content_tr: '',
  duration_minutes: 0, pdf_file_id: '', pdf_filename: '', pdfs: [], materials: [],
  materials_required: false,
  video_min_watch_percent: 80, quiz_passing_score: 70, quiz_questions: [],
  // Faz 4e — structured content for new lesson types
  lesson_content: null,
};

// ─── Drag-drop: sortable item sarici ─────────────────────────────────────────
function SortableItem({ id, children, dragHandleProps: _dragHandleProps }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
}

// ─── Modul satiri bileşeni ─────────────────────────────────────────────────────
function ModuleRow({
  module, courseId, lang, t,
  onEdit, onDelete, onLessonEdit, onLessonDelete, onLessonAdd,
  listeners,
}) {
  const [expanded, setExpanded] = useState(false);
  const lessons = module.lessons || [];

  // Lesson reorder mutation
  const reorderLessonsMut = useReorderLessons(courseId);
  const sensors = useSensors(useSensor(PointerSensor));

  const handleLessonDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = lessons.findIndex((l) => l.lesson_id === active.id);
      const newIndex = lessons.findIndex((l) => l.lesson_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(lessons, oldIndex, newIndex);
      reorderLessonsMut.mutate({
        moduleId: module.module_id,
        lessonIds: reordered.map((l) => l.lesson_id),
      });
    },
    // reorderLessonsMut is a react-query mutation object — it's stable but
    // exhaustive-deps can't prove it, so disable for this line only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lessons, module.module_id],
  );

  return (
    <div className="bg-white rounded-xl border-2 border-[var(--border-light)] overflow-hidden">
      {/* Module header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[var(--bg-app)]">
        <span
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-[var(--text-muted)] hover:text-[var(--brand-primary)] p-1"
          title={t('admin.courseManager.drag_hint')}
        >
          <GripVertical size={16} />
        </span>
        <span className="w-6 h-6 rounded-full bg-[var(--brand-primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
          {module.order}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate text-[var(--text-main)]">
            {lang === 'tr' ? (module.title_tr || module.title) : (module.title || module.title_tr)}
          </p>
          {module.description && (
            <p className="text-xs text-[var(--text-muted)] truncate">{module.description}</p>
          )}
        </div>
        <StatusBadge
          variant="neutral"
          label={`${lessons.length} ${t('admin.courseManager.col_lessons')}`}
        />
        <button
          onClick={() => onEdit(module)}
          className="p-1.5 rounded-lg hover:bg-white text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors"
          title={t('admin.courseManager.module_edit')}
        >
          <Edit2 size={14} />
        </button>
        <button
          onClick={() => onDelete(module)}
          className="p-1.5 rounded-lg hover:bg-red-50 text-[var(--text-muted)] hover:text-[var(--brand-danger)] transition-colors"
          title={t('admin.courseManager.module_delete')}
        >
          <Trash2 size={14} />
        </button>
        <button
          onClick={() => setExpanded((e) => !e)}
          className="p-1.5 rounded-lg hover:bg-white text-[var(--text-muted)] transition-colors"
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Lessons inside module — nested drag-drop */}
      {expanded && (
        <div className="px-4 pb-3 pt-2 space-y-1.5">
          {lessons.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] py-2 text-center">
              {t('admin.courseManager.lesson_empty')}
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleLessonDragEnd}
            >
              <SortableContext
                items={lessons.map((l) => l.lesson_id)}
                strategy={verticalListSortingStrategy}
              >
                {lessons.map((lesson) => (
                  <SortableItem key={lesson.lesson_id} id={lesson.lesson_id}>
                    {({ listeners: lsnListeners }) => (
                      <div className="flex items-center gap-2 py-1.5 px-2 rounded-lg border border-[var(--border-light)] bg-gray-50 hover:bg-white transition-colors group">
                        <span
                          {...lsnListeners}
                          className="cursor-grab active:cursor-grabbing text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <GripVertical size={14} />
                        </span>
                        <span className="text-xs font-bold text-[var(--text-muted)] w-5 text-center">
                          {lesson.order}
                        </span>
                        {/* Type icon */}
                        {(() => {
                          const lt = LESSON_TYPES.find((x) => x.id === lesson.lesson_type);
                          const Icon = lt?.icon || BookOpen;
                          return <Icon size={14} className="text-[var(--text-muted)] flex-shrink-0" />;
                        })()}
                        <span className="flex-1 text-xs font-semibold truncate text-[var(--text-main)]">
                          {lang === 'tr'
                            ? (lesson.title_tr || lesson.title)
                            : (lesson.title || lesson.title_tr)}
                        </span>
                        <button
                          onClick={() => onLessonEdit(lesson)}
                          className="p-1 rounded hover:text-[var(--brand-primary)] text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity"
                          title={t('admin.courseManager.lesson_edit')}
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          onClick={() => onLessonDelete(lesson)}
                          className="p-1 rounded hover:text-[var(--brand-danger)] text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity"
                          title={t('admin.courseManager.lesson_delete')}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>
          )}
          <button
            onClick={() => onLessonAdd(module)}
            className="w-full text-xs text-[var(--brand-primary)] hover:text-[var(--brand-primary)]/80 font-bold flex items-center justify-center gap-1 py-1.5 border border-dashed border-[var(--brand-primary)]/30 rounded-lg hover:bg-[var(--brand-primary)]/5 transition-colors mt-1"
          >
            <Plus size={12} /> {t('admin.courseManager.btn_add_lesson')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Vimeo Library Picker Modal ───────────────────────────────────────────────
function VimeoLibraryPicker({ onSelect, onClose, authHeaders, t, lang }) {
  const [accounts, setAccounts] = useState([]);
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [hoveredId, setHoveredId] = useState(null);

  const formatDuration = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    setLoadingFolders(true);
    axios.get(`${VIMEO_API}/admin/vimeo/accounts`, { headers: authHeaders() })
      .then(res => {
        const accs = res.data.accounts || [];
        setAccounts(accs);
        if (accs.length > 0) {
          setActiveAccountId(accs[0].id);
          fetchFolders(accs[0].id);
        } else {
          setLoadingFolders(false);
        }
      })
      .catch(() => setLoadingFolders(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFolders = (accountId) => {
    setLoadingFolders(true);
    axios.get(`${VIMEO_API}/admin/vimeo/folders?account=${accountId}`, { headers: authHeaders() })
      .then(res => setFolders(res.data.folders || []))
      .catch(() => setFolders([]))
      .finally(() => setLoadingFolders(false));
  };

  const openFolder = (folder) => {
    setSelectedFolder(folder);
    setLoadingVideos(true);
    axios.get(`${VIMEO_API}/admin/vimeo/folders/${folder.folder_id}/videos?page=1&per_page=100&account=${activeAccountId || ''}`, { headers: authHeaders() })
      .then(res => setVideos(res.data.videos || []))
      .catch(() => setVideos([]))
      .finally(() => setLoadingVideos(false));
  };

  const searchAll = (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;
    setSelectedFolder(null);
    setLoadingVideos(true);
    axios.get(`${VIMEO_API}/admin/vimeo/videos?page=1&per_page=100&search=${encodeURIComponent(searchInput)}&account=${activeAccountId || ''}`, { headers: authHeaders() })
      .then(res => setVideos(res.data.videos || []))
      .catch(() => setVideos([]))
      .finally(() => setLoadingVideos(false));
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="vimeo-picker-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden border-2 border-[var(--border-light)] shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="vimeo-picker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-light)] flex-shrink-0">
            <div className="flex items-center gap-2">
              {selectedFolder && (
                <button
                  onClick={() => { setSelectedFolder(null); setVideos([]); }}
                  className="p-1 rounded-lg hover:bg-gray-100 text-[var(--text-muted)] mr-1"
                  title={t('admin.lessons.vimeoPicker.backToFolders')}
                >
                  <ChevronLeft size={16} />
                </button>
              )}
              <VideoIcon size={18} className="text-sky-500" />
              <span className="font-bold text-sm text-[var(--text-main)]">
                {selectedFolder ? selectedFolder.name : t('admin.lessons.vimeoPicker.title')}
              </span>
            </div>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-[var(--text-muted)]">
              <X size={18} />
            </button>
          </div>

          {/* Account tabs (if multiple) */}
          {accounts.length > 1 && (
            <div className="flex gap-2 px-5 pt-3 flex-shrink-0">
              {accounts.map(acc => (
                <button key={acc.id}
                  onClick={() => { setActiveAccountId(acc.id); setSelectedFolder(null); setVideos([]); fetchFolders(acc.id); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${activeAccountId === acc.id ? 'bg-sky-500 text-white' : 'bg-gray-100 text-[var(--text-muted)] hover:bg-gray-200'}`}
                >
                  {acc.name}
                </button>
              ))}
            </div>
          )}

          {/* Search bar */}
          <form onSubmit={searchAll} className="flex gap-2 px-5 py-3 flex-shrink-0">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={t('admin.lessons.vimeoPicker.search')}
                className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[var(--border-light)] text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
              />
            </div>
            <button type="submit" className="px-4 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-xs font-bold hover:opacity-90 transition-opacity">
              {lang === 'tr' ? 'Ara' : 'Search'}
            </button>
          </form>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 pb-5">
            {loadingFolders ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-7 h-7 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-3 text-sm text-[var(--text-muted)]">{t('admin.lessons.vimeoPicker.loading')}</span>
              </div>
            ) : !selectedFolder && videos.length === 0 ? (
              /* Folder list */
              folders.length === 0 ? (
                <div className="py-10 text-center">
                  <Folder size={36} className="mx-auto text-[var(--text-muted)] mb-2" />
                  <p className="text-sm text-[var(--text-muted)] font-semibold">{t('admin.lessons.vimeoPicker.emptyFolders')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {folders.filter(f => (f.video_count ?? f.videos_count ?? 0) > 0).map((f, i) => (
                    <button
                      key={f.folder_id || i}
                      onClick={() => openFolder(f)}
                      className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)] hover:border-sky-400 bg-white text-left transition-colors group"
                    >
                      <FolderOpen size={20} className="text-sky-400 flex-shrink-0 group-hover:text-sky-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-xs truncate">{f.name}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">{f.video_count ?? f.videos_count ?? 0} video</p>
                      </div>
                    </button>
                  ))}
                </div>
              )
            ) : loadingVideos ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-7 h-7 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-3 text-sm text-[var(--text-muted)]">{t('admin.lessons.vimeoPicker.loadingVideos')}</span>
              </div>
            ) : videos.length === 0 ? (
              <div className="py-10 text-center">
                <VideoIcon size={36} className="mx-auto text-[var(--text-muted)] mb-2" />
                <p className="text-sm text-[var(--text-muted)] font-semibold">{t('admin.lessons.vimeoPicker.empty')}</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {videos.map((v, i) => (
                  <button
                    key={v.vimeo_id || i}
                    onClick={() => onSelect(v)}
                    onMouseEnter={() => setHoveredId(v.vimeo_id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${hoveredId === v.vimeo_id ? 'border-[var(--brand-primary)] bg-blue-50' : 'border-[var(--border-light)] bg-white hover:border-gray-300'}`}
                    data-testid={`vimeo-picker-video-${i}`}
                  >
                    <div className="w-16 h-10 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0">
                      {v.thumbnail
                        ? <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center"><Play size={14} className="text-gray-500" /></div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs truncate text-[var(--text-main)]">{v.name}</p>
                      {v.description && <p className="text-[10px] text-[var(--text-muted)] truncate">{v.description}</p>}
                    </div>
                    <span className="text-xs font-bold text-[var(--text-muted)] flex items-center gap-1 flex-shrink-0">
                      <Clock size={11} /> {formatDuration(v.duration || 0)}
                    </span>
                    {hoveredId === v.vimeo_id && (
                      <span className="px-2 py-1 rounded-lg bg-[var(--brand-primary)] text-white text-[10px] font-bold flex-shrink-0">
                        {t('admin.lessons.vimeoPicker.confirm')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Quiz editor ──────────────────────────────────────────────────────────────
function QuizEditor({ questions, onChange, t }) {
  const addQuestion = () => {
    onChange([
      ...questions,
      { question: '', options: ['', '', '', ''], correct_answer: '' },
    ]);
  };

  const removeQuestion = (i) => {
    onChange(questions.filter((_, idx) => idx !== i));
  };

  const updateQuestion = (i, field, value) => {
    const next = questions.map((q, idx) =>
      idx === i ? { ...q, [field]: value } : q,
    );
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {questions.length === 0 && (
        <EmptyState
          icon={<Zap size={36} className="text-amber-400" />}
          title={t('admin.courseManager.quiz_empty')}
          className="py-6"
        />
      )}
      {questions.map((q, i) => (
        <div key={i} className="bg-[var(--bg-app)] rounded-xl border-2 border-[var(--border-light)] p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs font-bold text-[var(--text-muted)]">#{i + 1}</span>
            <button
              onClick={() => removeQuestion(i)}
              className="text-xs text-[var(--brand-danger)] hover:opacity-70 font-bold flex items-center gap-1"
            >
              <X size={12} /> {t('admin.courseManager.quiz_remove')}
            </button>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {t('admin.courseManager.quiz_question')}
            </label>
            <Input
              value={q.question}
              onChange={(e) => updateQuestion(i, 'question', e.target.value)}
              className="rounded-xl border-2 border-[var(--border-light)] text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {t('admin.courseManager.quiz_options')}
            </label>
            <textarea
              rows={4}
              value={q.options?.join('\n') || ''}
              onChange={(e) =>
                updateQuestion(i, 'options', e.target.value.split('\n'))
              }
              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-[var(--text-muted)]">
              {t('admin.courseManager.quiz_correct')}
            </label>
            <Input
              value={q.correct_answer}
              onChange={(e) => updateQuestion(i, 'correct_answer', e.target.value)}
              className="rounded-xl border-2 border-[var(--border-light)] text-sm"
            />
          </div>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={addQuestion}
        className="rounded-xl border-2 border-dashed border-[var(--border-heavy)] text-[var(--text-muted)] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] font-bold w-full"
      >
        <Plus size={14} className="mr-1" /> {t('admin.courseManager.quiz_add')}
      </Button>
    </div>
  );
}

// ─── Ana bileşen ──────────────────────────────────────────────────────────────
export default function AdminCourseManager() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  // ── Filtreler ──────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({});

  const filters = {
    search: search || undefined,
    category: activeFilters.category || undefined,
    level: activeFilters.level || undefined,
    is_interactive:
      activeFilters.is_interactive === 'true'
        ? true
        : activeFilters.is_interactive === 'false'
        ? false
        : undefined,
  };

  // ── React Query: kurs listesi ──────────────────────────────────────────────
  const { data: courses = [], isLoading: coursesLoading } = useCourses(filters);

  // ── Dialog state ───────────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState('general');
  const [selectedCourse, setSelectedCourse] = useState(null); // null = yeni kurs

  // ── Course form ────────────────────────────────────────────────────────────
  const [courseForm, setCourseForm] = useState(DEFAULT_COURSE_FORM);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const thumbRef = useRef(null);

  // ── Module state ───────────────────────────────────────────────────────────
  const [moduleFormOpen, setModuleFormOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState(DEFAULT_MODULE_FORM);
  const [deletingModule, setDeletingModule] = useState(null);

  // ── Lesson state ───────────────────────────────────────────────────────────
  const [lessonFormOpen, setLessonFormOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState(DEFAULT_LESSON_FORM);
  const [deletingLesson, setDeletingLesson] = useState(null);
  const [quizLesson, setQuizLesson] = useState(null); // quiz tab seçili ders
  const [vimeoPickerOpen, setVimeoPickerOpen] = useState(false);

  // ── Final sınavı yapılandırma modalı ────────────────────────────────────────
  const [examModalCourseId, setExamModalCourseId] = useState(null);

  // ── Confirm dialogs ────────────────────────────────────────────────────────
  const [deletingCourse, setDeletingCourse] = useState(null);

  // ── React Query: secili kurs detay + moduller ──────────────────────────────
  const courseId = selectedCourse?.course_id;
  const { data: courseDetail } = useCourse(courseId);
  const { data: modules = [], isLoading: modulesLoading } = useCourseModules(courseId);
  const lessons = courseDetail?.lessons || [];

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createCourseMut = useCreateCourse();
  const updateCourseMut = useUpdateCourse(courseId);
  const deleteCourseMut = useDeleteCourse();

  const createModuleMut = useCreateModule(courseId);
  const updateModuleMut = useUpdateModule(courseId);
  const deleteModuleMut = useDeleteModule(courseId);
  const reorderModulesMut = useReorderModules(courseId);

  const createLessonMut = useCreateLesson(courseId);
  const updateLessonMut = useUpdateLesson(courseId);
  const deleteLessonMut = useDeleteLesson(courseId);

  // ── Dialog aç/kapat ────────────────────────────────────────────────────────
  const openNewCourse = () => {
    setSelectedCourse(null);
    setCourseForm(DEFAULT_COURSE_FORM);
    setThumbnailPreview('');
    setDialogTab('general');
    setDialogOpen(true);
  };

  const openEditCourse = (course) => {
    setSelectedCourse(course);
    setCourseForm({
      title: course.title || '',
      title_tr: course.title_tr || '',
      description: course.description || '',
      description_tr: course.description_tr || '',
      category: course.category || 'programming',
      subcategory: course.subcategory || '',
      price: course.price || 0,
      currency: course.currency || 'TRY',
      thumbnail: course.thumbnail || '',
      vimeo_intro_url: course.vimeo_intro_url || '',
      level: course.level || 'beginner',
      language: course.language || 'tr',
      is_interactive: course.is_interactive || false,
      level_required: course.level_required || null,
      certificate_info: course.certificate_info || { title: '', subtitle: '' },
      sinav_giris_gun_sayisi: course.sinav_giris_gun_sayisi || 0,
      sertifika_adi_tr: course.sertifika_adi_tr || '',
      sertifika_adi_en: course.sertifika_adi_en || '',
    });
    setThumbnailPreview(course.thumbnail || '');
    setDialogTab('general');
    setDialogOpen(true);
  };

  const openModulesTab = (course) => {
    openEditCourse(course);
    setDialogTab('modules');
  };

  // ── Thumbnail upload ───────────────────────────────────────────────────────
  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/upload/thumbnail', fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setCourseForm((p) => ({ ...p, thumbnail: url }));
      setThumbnailPreview(url);
    } catch {
      setThumbnailPreview('');
    } finally {
      setUploading(false);
    }
  };

  // ── Kurs kaydet ────────────────────────────────────────────────────────────
  const saveCourse = async () => {
    if (!courseForm.title) {
      toast.warning(t('admin.courseManager.validation_title_required'));
      return;
    }
    try {
      if (selectedCourse) {
        await updateCourseMut.mutateAsync(courseForm);
        toast.success(t('admin.courseManager.save_success_update'));
      } else {
        const created = await createCourseMut.mutateAsync(courseForm);
        setSelectedCourse(created?.course || created);
        toast.success(t('admin.courseManager.save_success_create'));
        setDialogTab('modules');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  // ── Kurs sil ───────────────────────────────────────────────────────────────
  const handleDeleteCourse = async () => {
    if (!deletingCourse) return;
    try {
      await deleteCourseMut.mutateAsync(deletingCourse.course_id);
      toast.success(t('admin.courseManager.action_delete') + ': OK');
      setDeletingCourse(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  // ── Module: Kaydet ─────────────────────────────────────────────────────────
  const saveModule = async () => {
    if (!moduleForm.title && !moduleForm.title_tr) return;
    try {
      if (editingModule) {
        await updateModuleMut.mutateAsync({
          moduleId: editingModule.module_id,
          data: moduleForm,
        });
      } else {
        await createModuleMut.mutateAsync(moduleForm);
      }
      setModuleFormOpen(false);
      setEditingModule(null);
      setModuleForm(DEFAULT_MODULE_FORM);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteModule = async () => {
    if (!deletingModule) return;
    try {
      await deleteModuleMut.mutateAsync(deletingModule.module_id);
      setDeletingModule(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  // ── Module drag-drop reorder ───────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor));

  const handleModuleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = modules.findIndex((m) => m.module_id === active.id);
      const newIndex = modules.findIndex((m) => m.module_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(modules, oldIndex, newIndex);
      reorderModulesMut.mutate(reordered.map((m) => m.module_id));
    },
    [modules, reorderModulesMut],
  );

  // ── Lesson: Kaydet ─────────────────────────────────────────────────────────
  const saveLesson = async () => {
    // EN veya TR başlığından en az biri zorunlu
    if (!lessonForm.title_en && !lessonForm.title_tr && !lessonForm.title) {
      toast.warning(t('admin.courseManager.lesson_validation_title'));
      return;
    }
    if (!courseId) {
      toast.warning(t('admin.courseManager.lesson_validation_course'));
      return;
    }
    // Backend LessonCreate.title str zorunlu — title_tr || title_en || title'dan türet (geriye uyum)
    const derivedTitle = lessonForm.title_tr || lessonForm.title_en || lessonForm.title || '';
    const payload = {
      ...lessonForm,
      title: derivedTitle,
      title_tr: lessonForm.title_tr || derivedTitle,
      // title_en LessonCreate'te tanımlı — backend persist eder
      title_en: lessonForm.title_en || '',
    };
    try {
      if (editingLesson) {
        await updateLessonMut.mutateAsync({
          lessonId: editingLesson.lesson_id,
          data: payload,
        });
      } else {
        await createLessonMut.mutateAsync(payload);
      }
      setLessonFormOpen(false);
      setEditingLesson(null);
      setLessonForm(DEFAULT_LESSON_FORM);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteLesson = async () => {
    if (!deletingLesson) return;
    try {
      await deleteLessonMut.mutateAsync(deletingLesson.lesson_id);
      setDeletingLesson(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  // ── PDF Upload (lesson form'da) ────────────────────────────────────────────
  const pdfRef = useRef(null);
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post('/upload/pdf', fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const newPdf = { file_id: res.data.file_id, filename: res.data.filename };
      setLessonForm((p) => ({
        ...p,
        pdf_file_id: p.pdf_file_id || res.data.file_id,
        pdf_filename: p.pdf_filename || res.data.filename,
        pdfs: [...(p.pdfs || []), newPdf],
      }));
    } catch {
      toast.error('PDF upload failed');
    } finally {
      if (pdfRef.current) pdfRef.current.value = '';
    }
  };

  // ── DataTable kolon tanimi ─────────────────────────────────────────────────
  const columns = [
    {
      key: 'thumbnail_cell',
      label: t('admin.courseManager.col_course'),
      width: '40%',
      render: (_, row) => (
        <div className="flex items-center gap-3 py-1">
          <img
            src={row.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=80'}
            alt=""
            className="w-14 h-10 rounded-lg object-cover flex-shrink-0 border border-[var(--border-light)]"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-bold text-sm text-[var(--text-main)] truncate">
                {row.title || row.title_tr}
              </span>
              {row.is_interactive && (
                <Zap size={12} className="text-amber-500 flex-shrink-0" />
              )}
              {row.category === 'cabin_crew' && (
                <Plane size={12} className="text-sky-500 flex-shrink-0" />
              )}
              {(row.lessons_count || 0) === 0 && (
                <StatusBadge variant="warning" label={t('admin.courseManager.draft_badge')} />
              )}
            </div>
            {(row.title_tr || row.description) && (
              <p className="text-xs text-[var(--text-muted)] truncate max-w-xs">
                {row.title_tr || row.description}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      label: t('admin.courseManager.col_category'),
      sortable: true,
      render: (v) => (
        <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs font-bold capitalize">
          {v}
        </span>
      ),
    },
    {
      key: 'level',
      label: t('admin.courseManager.col_level'),
      sortable: true,
      render: (v) => (
        <StatusBadge
          variant={v === 'beginner' ? 'success' : v === 'intermediate' ? 'warning' : 'danger'}
          label={v || '—'}
        />
      ),
    },
    {
      key: 'lessons_count',
      label: t('admin.courseManager.col_lessons'),
      sortable: true,
      render: (v) => (
        <span className="flex items-center gap-1 text-sm font-semibold text-[var(--text-muted)]">
          <BookOpen size={13} /> {v || 0}
        </span>
      ),
    },
    {
      key: 'enrolled_count',
      label: t('admin.courseManager.col_enrolled'),
      sortable: true,
      render: (v) => (
        <span className="flex items-center gap-1 text-sm font-semibold text-[var(--text-muted)]">
          <Users size={13} /> {v || 0}
        </span>
      ),
    },
    {
      key: 'price',
      label: t('admin.courseManager.col_price'),
      sortable: true,
      render: (v, row) => (
        <span className="font-display font-bold text-[var(--brand-primary)] text-sm">
          {v} {row.currency || 'TRY'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '60px',
      render: (_, row) => (
        <ActionMenu
          align="end"
          items={[
            {
              label: t('admin.courseManager.action_edit'),
              icon: Edit2,
              onClick: () => openEditCourse(row),
            },
            {
              label: t('admin.courseManager.action_modules'),
              icon: BookOpen,
              onClick: () => openModulesTab(row),
            },
            {
              label: lang === 'tr' ? 'Final Sınavı' : 'Final Exam',
              icon: FileCheck,
              onClick: () => setExamModalCourseId(row.course_id),
            },
            { separator: true },
            {
              label: t('admin.courseManager.action_delete'),
              icon: Trash2,
              variant: 'danger',
              onClick: () => setDeletingCourse(row),
            },
          ]}
        />
      ),
    },
  ];

  // ── FilterBar tanimlari ────────────────────────────────────────────────────
  const filterDefs = [
    {
      key: 'category',
      label: t('admin.courseManager.filter_category'),
      options: CATEGORIES.map((c) => ({
        value: c.id,
        label: lang === 'tr' ? c.label_tr : c.label_en,
      })),
    },
    {
      key: 'level',
      label: t('admin.courseManager.filter_level'),
      options: LEVELS.map((l) => ({
        value: l,
        label: l.charAt(0).toUpperCase() + l.slice(1),
      })),
    },
    {
      key: 'is_interactive',
      label: t('admin.courseManager.filter_interactive'),
      options: [
        { value: 'true', label: t('admin.courseManager.filter_interactive_yes') },
        { value: 'false', label: t('admin.courseManager.filter_interactive_no') },
      ],
    },
  ];

  const handleFilterChange = (key, value) => {
    setActiveFilters((p) => ({ ...p, [key]: value || undefined }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearch('');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AdminLayout
      title="Course Manager"
      titleTr="Kurs Yoneticisi"
    >
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <PageHeader
        breadcrumbs={[
          { label: t('admin.courseManager.breadcrumb_admin'), href: '/admin' },
          { label: t('admin.courseManager.breadcrumb_courses') },
        ]}
        title={t('admin.courseManager.title')}
        subtitle={t('admin.courseManager.subtitle')}
        actions={
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              onClick={openNewCourse}
              className="btn-primary flex items-center gap-2 font-bold rounded-xl px-5 py-2.5"
              data-testid="new-course-btn"
            >
              <Plus size={16} /> {t('admin.courseManager.btn_new_course')}
            </Button>
          </motion.div>
        }
      />

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="mb-4">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('admin.courseManager.filter_search_placeholder')}
          filters={filterDefs}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearAll={clearFilters}
        />
      </div>

      {/* ── Data table ──────────────────────────────────────────────────── */}
      <DataTable
        columns={columns}
        data={courses}
        loading={coursesLoading}
        rowKey="course_id"
        pageSize={20}
        emptyIcon={<BookOpen size={40} strokeWidth={1.5} />}
        emptyTitle={t('admin.courseManager.empty_title')}
        emptyDescription={t('admin.courseManager.empty_desc')}
      />

      {/* ── Vimeo Library Picker ─────────────────────────────────────── */}
      {vimeoPickerOpen && (
        <VimeoLibraryPicker
          authHeaders={authHeaders}
          t={t}
          lang={lang}
          onClose={() => setVimeoPickerOpen(false)}
          onSelect={(video) => {
            const cleanName = (video.name || '').replace(/\.\w+$/, '').trim();
            setLessonForm((p) => ({
              ...p,
              vimeo_url: video.embed_url || video.link || '',
              duration_minutes: video.duration ? Math.ceil(video.duration / 60) : p.duration_minutes,
              title: p.title || cleanName,
              title_tr: p.title_tr || cleanName,
              title_en: p.title_en || '',
            }));
            setVimeoPickerOpen(false);
          }}
        />
      )}

      {/* ── Final sınavı yapılandırma modalı ────────────────────────────── */}
      <AdminExamConfigModal
        isOpen={!!examModalCourseId}
        onClose={() => setExamModalCourseId(null)}
        courseId={examModalCourseId}
        authHeaders={authHeaders}
        lang={lang}
      />

      {/* ── Kurs sil onay ───────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deletingCourse}
        onOpenChange={(o) => !o && setDeletingCourse(null)}
        title={t('admin.courseManager.delete_title')}
        description={t('admin.courseManager.delete_desc')}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleDeleteCourse}
        loading={deleteCourseMut.isPending}
      />

      {/* ── Kurs edit dialog ────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl w-full rounded-2xl border-2 border-[var(--border-light)] max-h-[92vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[var(--border-light)]">
            <DialogTitle className="font-display text-xl font-bold text-[var(--text-main)]">
              {selectedCourse
                ? t('admin.courseManager.dialog_edit_title')
                : t('admin.courseManager.dialog_new_title')}
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <Tabs
            value={dialogTab}
            onValueChange={setDialogTab}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="mx-6 mt-4 mb-2 rounded-xl bg-[var(--bg-app)] border-2 border-[var(--border-light)] h-auto p-1 flex-shrink-0 flex flex-wrap gap-1">
              {[
                { id: 'general',     label: t('admin.courseManager.tab_general') },
                { id: 'modules',     label: t('admin.courseManager.tab_modules') },
                { id: 'lessons',     label: t('admin.courseManager.tab_lessons') },
                { id: 'quiz',        label: t('admin.courseManager.tab_quiz') },
                { id: 'certificate', label: t('admin.courseManager.tab_certificate') },
              ].map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="rounded-lg text-xs font-bold px-3 py-1.5 data-[state=active]:bg-white data-[state=active]:text-[var(--brand-primary)] data-[state=active]:shadow-sm"
                  disabled={!selectedCourse && tab.id !== 'general'}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 overflow-y-auto">
              {/* ──────── TAB: GENEL ──────────────────────────────────── */}
              <TabsContent value="general" className="p-6 space-y-4 mt-0">
                {/* Thumbnail */}
                <FormSection title={t('admin.courseManager.section_thumbnail')} columns={1}>
                  <div className="flex items-start gap-4">
                    <div className="w-40 h-28 rounded-xl border-2 border-dashed border-[var(--border-light)] bg-gray-50 overflow-hidden flex items-center justify-center relative flex-shrink-0">
                      {thumbnailPreview ? (
                        <>
                          <img src={thumbnailPreview} alt="" className="w-full h-full object-cover" />
                          <button
                            onClick={() => { setThumbnailPreview(''); setCourseForm((p) => ({ ...p, thumbnail: '' })); }}
                            className="absolute top-1 right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow border"
                          >
                            <X size={10} />
                          </button>
                        </>
                      ) : (
                        <Image size={28} className="text-[var(--text-muted)]" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input type="file" ref={thumbRef} accept="image/*" onChange={handleThumbUpload} className="hidden" />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => thumbRef.current?.click()}
                        disabled={uploading}
                        className="rounded-xl border-2 text-xs flex items-center gap-1 font-bold"
                        data-testid="upload-thumb-btn"
                      >
                        <Upload size={14} />
                        {uploading ? '...' : t('admin.courseManager.btn_upload_thumb')}
                      </Button>
                      <Input
                        value={courseForm.thumbnail}
                        onChange={(e) => {
                          setCourseForm((p) => ({ ...p, thumbnail: e.target.value }));
                          if (e.target.value) setThumbnailPreview(e.target.value);
                        }}
                        placeholder="https://..."
                        className="text-xs rounded-xl border-2 border-[var(--border-light)]"
                      />
                      {courseForm.thumbnail && (
                        <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                          <CheckCircle size={10} /> Image set
                        </p>
                      )}
                    </div>
                  </div>
                </FormSection>

                {/* Temel bilgiler */}
                <FormSection title={t('admin.courseManager.section_basic')} columns={2}>
                  <FormField label={t('admin.courseManager.field_title_en')} required>
                    <Input
                      value={courseForm.title}
                      onChange={(e) => setCourseForm((p) => ({ ...p, title: e.target.value }))}
                      className="rounded-xl border-2 border-[var(--border-light)]"
                      data-testid="course-title"
                    />
                  </FormField>
                  <FormField label={t('admin.courseManager.field_title_tr')}>
                    <Input
                      value={courseForm.title_tr}
                      onChange={(e) => setCourseForm((p) => ({ ...p, title_tr: e.target.value }))}
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={t('admin.courseManager.field_desc_en')} span={2}>
                    <textarea
                      rows={2}
                      value={courseForm.description}
                      onChange={(e) => setCourseForm((p) => ({ ...p, description: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
                    />
                  </FormField>
                  <FormField label={t('admin.courseManager.field_desc_tr')} span={2}>
                    <textarea
                      rows={2}
                      value={courseForm.description_tr}
                      onChange={(e) => setCourseForm((p) => ({ ...p, description_tr: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
                    />
                  </FormField>
                </FormSection>

                {/* Ayarlar */}
                <FormSection title={t('admin.courseManager.section_settings')} columns={2}>
                  <FormField label={t('admin.courseManager.field_category')}>
                    <select
                      value={courseForm.category}
                      onChange={(e) => setCourseForm((p) => ({ ...p, category: e.target.value, subcategory: '' }))}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c.id} value={c.id}>
                          {lang === 'tr' ? c.label_tr : c.label_en}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  {courseForm.category === 'hr_training' && (
                    <FormField label={t('admin.courseManager.field_subcategory')}>
                      <select
                        value={courseForm.subcategory}
                        onChange={(e) => setCourseForm((p) => ({ ...p, subcategory: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                      >
                        <option value="">—</option>
                        {HR_SUBCATEGORIES.map((s) => (
                          <option key={s.id} value={s.id}>
                            {lang === 'tr' ? s.label_tr : s.label_en}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  )}
                  <FormField label={t('admin.courseManager.field_level')}>
                    <select
                      value={courseForm.level}
                      onChange={(e) => setCourseForm((p) => ({ ...p, level: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                    >
                      {LEVELS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label={t('admin.courseManager.field_price')}>
                    <Input
                      type="number"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm((p) => ({ ...p, price: Number(e.target.value) }))}
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={lang === 'tr' ? 'Sınav Giriş Süresi (gün)' : 'Exam Entry Period (days)'}>
                    <Input
                      type="number"
                      value={courseForm.sinav_giris_gun_sayisi || 0}
                      onChange={(e) => setCourseForm((p) => ({ ...p, sinav_giris_gun_sayisi: Number(e.target.value) }))}
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={lang === 'tr' ? 'Sertifika Adı (TR)' : 'Certificate Name (TR)'}>
                    <Input
                      value={courseForm.sertifika_adi_tr || ''}
                      onChange={(e) => setCourseForm((p) => ({ ...p, sertifika_adi_tr: e.target.value }))}
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={lang === 'tr' ? 'Sertifika Adı (EN)' : 'Certificate Name (EN)'}>
                    <Input
                      value={courseForm.sertifika_adi_en || ''}
                      onChange={(e) => setCourseForm((p) => ({ ...p, sertifika_adi_en: e.target.value }))}
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={t('admin.courseManager.field_currency')}>
                    <select
                      value={courseForm.currency}
                      onChange={(e) => setCourseForm((p) => ({ ...p, currency: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                    >
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </FormField>
                  <FormField label={t('admin.courseManager.field_language')}>
                    <select
                      value={courseForm.language}
                      onChange={(e) => setCourseForm((p) => ({ ...p, language: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                    >
                      {LANGUAGES.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                  </FormField>
                  <FormField label={t('admin.courseManager.field_vimeo_intro')} span={2}>
                    <Input
                      value={courseForm.vimeo_intro_url}
                      onChange={(e) => setCourseForm((p) => ({ ...p, vimeo_intro_url: e.target.value }))}
                      placeholder="https://vimeo.com/..."
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={t('admin.courseManager.field_interactive')}>
                    <div className="flex items-center gap-3 pt-1">
                      <Switch
                        checked={courseForm.is_interactive}
                        onCheckedChange={(v) => setCourseForm((p) => ({ ...p, is_interactive: v }))}
                      />
                      <span className="text-sm font-semibold text-[var(--text-muted)]">
                        {courseForm.is_interactive
                          ? t('admin.courseManager.filter_interactive_yes')
                          : t('admin.courseManager.filter_interactive_no')}
                      </span>
                    </div>
                  </FormField>
                  {courseForm.category === 'cabin_crew' && (
                    <FormField label={t('admin.courseManager.field_level_required')}>
                      <select
                        value={courseForm.level_required || ''}
                        onChange={(e) =>
                          setCourseForm((p) => ({ ...p, level_required: e.target.value || null }))
                        }
                        className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                      >
                        <option value="">—</option>
                        {LEVEL_REQUIRED_OPTIONS.map((l) => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </FormField>
                  )}
                </FormSection>

                {/* Save button */}
                <div className="flex justify-end pt-2">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      onClick={saveCourse}
                      disabled={createCourseMut.isPending || updateCourseMut.isPending}
                      className="btn-primary flex items-center gap-2 rounded-xl px-6 font-bold"
                    >
                      <Save size={16} />
                      {(createCourseMut.isPending || updateCourseMut.isPending)
                        ? t('admin.courseManager.btn_saving')
                        : t('admin.courseManager.btn_save')}
                    </Button>
                  </motion.div>
                </div>
              </TabsContent>

              {/* ──────── TAB: MODULLER ───────────────────────────────── */}
              <TabsContent value="modules" className="p-6 mt-0">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-display font-bold text-base text-[var(--text-main)]">
                      {t('admin.courseManager.section_modules')}
                    </h3>
                    <p className="text-xs text-[var(--text-muted)] mt-0.5">
                      {t('admin.courseManager.section_modules_desc')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingModule(null);
                      setModuleForm({
                        ...DEFAULT_MODULE_FORM,
                        order: modules.length + 1,
                      });
                      setModuleFormOpen(true);
                    }}
                    className="btn-primary rounded-xl flex items-center gap-1 font-bold text-sm"
                    data-testid="add-module-btn"
                  >
                    <Plus size={14} /> {t('admin.courseManager.btn_add_module')}
                  </Button>
                </div>

                {/* Module form inline */}
                {moduleFormOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[var(--bg-app)] rounded-xl border-2 border-[var(--brand-primary)]/30 p-4 mb-4 space-y-3"
                  >
                    <h4 className="font-bold text-sm text-[var(--text-main)]">
                      {editingModule
                        ? t('admin.courseManager.module_edit')
                        : t('admin.courseManager.btn_add_module')}
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.module_title_en')}
                        </label>
                        <Input
                          value={moduleForm.title}
                          onChange={(e) => setModuleForm((p) => ({ ...p, title: e.target.value }))}
                          className="rounded-xl border-2 border-[var(--border-light)]"
                          data-testid="module-title-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.module_title_tr')}
                        </label>
                        <Input
                          value={moduleForm.title_tr}
                          onChange={(e) => setModuleForm((p) => ({ ...p, title_tr: e.target.value }))}
                          className="rounded-xl border-2 border-[var(--border-light)]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.module_description')}
                        </label>
                        <Input
                          value={moduleForm.description}
                          onChange={(e) => setModuleForm((p) => ({ ...p, description: e.target.value }))}
                          className="rounded-xl border-2 border-[var(--border-light)]"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.module_order')}
                        </label>
                        <Input
                          type="number"
                          value={moduleForm.order}
                          onChange={(e) => setModuleForm((p) => ({ ...p, order: Number(e.target.value) }))}
                          className="rounded-xl border-2 border-[var(--border-light)]"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setModuleFormOpen(false); setEditingModule(null); }}
                        className="rounded-xl border-2 font-bold"
                      >
                        {t('admin.courseManager.module_cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveModule}
                        disabled={createModuleMut.isPending || updateModuleMut.isPending}
                        className="btn-primary rounded-xl font-bold"
                        data-testid="save-module-btn"
                      >
                        <Save size={14} className="mr-1" />
                        {t('admin.courseManager.module_save')}
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Module listesi — drag-drop */}
                {modulesLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
                    ))}
                  </div>
                ) : modules.length === 0 ? (
                  <EmptyState
                    icon={<BookOpen size={40} strokeWidth={1.5} />}
                    title={t('admin.courseManager.module_empty')}
                    description={t('admin.courseManager.module_empty_desc')}
                  />
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleModuleDragEnd}
                  >
                    <SortableContext
                      items={modules.map((m) => m.module_id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {modules.map((mod) => (
                          <SortableItem key={mod.module_id} id={mod.module_id}>
                            {({ listeners: modListeners }) => (
                              <ModuleRow
                                module={mod}
                                courseId={courseId}
                                lang={lang}
                                t={t}
                                listeners={modListeners}
                                onEdit={(m) => {
                                  setEditingModule(m);
                                  setModuleForm({
                                    title: m.title || '',
                                    title_tr: m.title_tr || '',
                                    description: m.description || '',
                                    order: m.order || 1,
                                  });
                                  setModuleFormOpen(true);
                                }}
                                onDelete={(m) => setDeletingModule(m)}
                                onLessonEdit={(l) => {
                                  setEditingLesson(l);
                                  setLessonForm({
                                    title: l.title || '',
                                    title_tr: l.title_tr || l.title || '',
                                    title_en: l.title_en || '',
                                    description: l.description || '',
                                    description_tr: l.description_tr || '',
                                    order: l.order || 1,
                                    lesson_type: l.lesson_type || 'video',
                                    module_id: l.module_id || mod.module_id,
                                    module_title: l.module_title || mod.title || '',
                                    vimeo_url: l.vimeo_url || '',
                                    content: l.content || '',
                                    content_tr: l.content_tr || '',
                                    duration_minutes: l.duration_minutes || 0,
                                    pdf_file_id: l.pdf_file_id || '',
                                    pdf_filename: l.pdf_filename || '',
                                    pdfs: l.pdfs || [],
                                    materials: l.materials || [],
                                    materials_required: l.materials_required || false,
                                    video_min_watch_percent: l.video_min_watch_percent || 80,
                                    quiz_passing_score: l.quiz_passing_score || 70,
                                    quiz_questions: Array.isArray(l.quiz_questions) ? l.quiz_questions : [],
                                    lesson_content: l.lesson_content || null,
                                  });
                                  setDialogTab('lessons');
                                  setLessonFormOpen(true);
                                }}
                                onLessonDelete={(l) => setDeletingLesson(l)}
                                onLessonAdd={(m) => {
                                  setEditingLesson(null);
                                  setLessonForm({
                                    ...DEFAULT_LESSON_FORM,
                                    module_id: m.module_id,
                                    module_title: m.title || m.title_tr || '',
                                    order: (m.lessons?.length || 0) + 1,
                                  });
                                  setDialogTab('lessons');
                                  setLessonFormOpen(true);
                                }}
                              />
                            )}
                          </SortableItem>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
              </TabsContent>

              {/* ──────── TAB: DERSLER ────────────────────────────────── */}
              <TabsContent value="lessons" className="p-6 mt-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold text-base text-[var(--text-main)]">
                    {t('admin.courseManager.section_lessons')}
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditingLesson(null);
                      setLessonForm({
                        ...DEFAULT_LESSON_FORM,
                        order: lessons.length + 1,
                      });
                      setLessonFormOpen(true);
                    }}
                    className="btn-primary rounded-xl flex items-center gap-1 font-bold text-sm"
                    data-testid="add-lesson-btn"
                  >
                    <Plus size={14} /> {t('admin.courseManager.btn_add_lesson')}
                  </Button>
                </div>

                {/* Lesson form inline */}
                {lessonFormOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[var(--bg-app)] rounded-xl border-2 border-[var(--brand-primary)]/30 p-5 mb-4"
                  >
                    <h4 className="font-bold text-sm text-[var(--text-main)] mb-4">
                      {editingLesson
                        ? t('admin.courseManager.lesson_edit')
                        : t('admin.courseManager.btn_add_lesson')}
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {/* Baslik */}
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.lesson_title_en')} *
                        </label>
                        <Input
                          value={lessonForm.title_en}
                          onChange={(e) => setLessonForm((p) => ({ ...p, title_en: e.target.value }))}
                          className="rounded-xl border-2 border-[var(--border-light)]"
                          data-testid="lesson-title-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.lesson_title_tr')}
                        </label>
                        <Input
                          value={lessonForm.title_tr}
                          onChange={(e) => setLessonForm((p) => ({ ...p, title_tr: e.target.value }))}
                          className="rounded-xl border-2 border-[var(--border-light)]"
                        />
                      </div>
                      {/* Ders tipi */}
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.lesson_type')}
                        </label>
                        <select
                          value={lessonForm.lesson_type}
                          onChange={(e) =>
                            setLessonForm((p) => ({
                              ...p,
                              lesson_type: e.target.value,
                              // Reset structured content when type changes
                              lesson_content: null,
                            }))
                          }
                          className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                        >
                          {LESSON_TYPES.map((lt) => (
                            <option key={lt.id} value={lt.id}>
                              {lang === 'tr' ? lt.label_tr : lt.label_en}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Modul */}
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.lesson_module')}
                        </label>
                        <select
                          value={lessonForm.module_id || ''}
                          onChange={(e) => {
                            const mod = modules.find((m) => m.module_id === e.target.value);
                            setLessonForm((p) => ({
                              ...p,
                              module_id: e.target.value,
                              module_title: mod?.title || mod?.title_tr || '',
                            }));
                          }}
                          className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                        >
                          <option value="">— {t('admin.courseManager.lesson_module')} —</option>
                          {modules.map((m) => (
                            <option key={m.module_id} value={m.module_id}>
                              {lang === 'tr' ? (m.title_tr || m.title) : (m.title || m.title_tr)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Sira */}
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.lesson_order')}
                        </label>
                        <Input
                          type="number"
                          value={lessonForm.order}
                          onChange={(e) =>
                            setLessonForm((p) => ({ ...p, order: Number(e.target.value) }))
                          }
                          className="rounded-xl border-2 border-[var(--border-light)]"
                        />
                      </div>
                      {/* Sure */}
                      <div>
                        <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                          {t('admin.courseManager.lesson_duration')}
                        </label>
                        <Input
                          type="number"
                          value={lessonForm.duration_minutes}
                          onChange={(e) =>
                            setLessonForm((p) => ({ ...p, duration_minutes: Number(e.target.value) }))
                          }
                          className="rounded-xl border-2 border-[var(--border-light)]"
                        />
                      </div>
                      {/* Video URL (video tipi icin) */}
                      {lessonForm.lesson_type === 'video' && (
                        <>
                          <div className="sm:col-span-2">
                            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                              {t('admin.courseManager.lesson_vimeo')}
                            </label>
                            <div className="flex gap-2 items-center">
                              <Input
                                value={lessonForm.vimeo_url}
                                onChange={(e) =>
                                  setLessonForm((p) => ({ ...p, vimeo_url: e.target.value }))
                                }
                                placeholder="https://vimeo.com/..."
                                className="rounded-xl border-2 border-[var(--border-light)] flex-1"
                              />
                              <span className="text-xs text-[var(--text-muted)] font-semibold flex-shrink-0">
                                {t('admin.courseManager.lesson_vimeo_or')}
                              </span>
                              <button
                                type="button"
                                onClick={() => setVimeoPickerOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-sky-400 text-sky-600 bg-sky-50 hover:bg-sky-100 text-xs font-bold flex-shrink-0 transition-colors"
                                data-testid="open-vimeo-picker-btn"
                              >
                                <VideoIcon size={14} />
                                {t('admin.lessons.vimeoPicker.button')}
                              </button>
                            </div>
                            {lessonForm.vimeo_url && (
                              <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 mt-1">
                                <CheckCircle size={10} /> {lessonForm.vimeo_url}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                              {t('admin.courseManager.lesson_watch_percent')}
                            </label>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={lessonForm.video_min_watch_percent}
                              onChange={(e) =>
                                setLessonForm((p) => ({
                                  ...p,
                                  video_min_watch_percent: Number(e.target.value),
                                }))
                              }
                              className="rounded-xl border-2 border-[var(--border-light)]"
                            />
                          </div>
                        </>
                      )}
                      {/* Text icerik */}
                      {lessonForm.lesson_type === 'text' && (
                        <>
                          <div className="sm:col-span-2">
                            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                              {t('admin.courseManager.lesson_content_en')}
                            </label>
                            <textarea
                              rows={3}
                              value={lessonForm.content}
                              onChange={(e) =>
                                setLessonForm((p) => ({ ...p, content: e.target.value }))
                              }
                              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                              {t('admin.courseManager.lesson_content_tr')}
                            </label>
                            <textarea
                              rows={3}
                              value={lessonForm.content_tr}
                              onChange={(e) =>
                                setLessonForm((p) => ({ ...p, content_tr: e.target.value }))
                              }
                              className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none resize-none"
                            />
                          </div>
                        </>
                      )}
                      {/* PDF upload */}
                      {lessonForm.lesson_type === 'pdf' && (
                        <div className="sm:col-span-2">
                          <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">PDF</label>
                          <input
                            type="file"
                            ref={pdfRef}
                            accept=".pdf"
                            onChange={handlePdfUpload}
                            className="hidden"
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => pdfRef.current?.click()}
                              className="rounded-xl border-2 font-bold text-xs"
                            >
                              <Upload size={12} className="mr-1" /> Upload PDF
                            </Button>
                            {lessonForm.pdf_filename && (
                              <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                                <CheckCircle size={12} /> {lessonForm.pdf_filename}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Interactive: passing score */}
                      {lessonForm.lesson_type === 'interactive' && (
                        <div>
                          <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                            {t('admin.courseManager.lesson_passing_score')}
                          </label>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            value={lessonForm.quiz_passing_score}
                            onChange={(e) =>
                              setLessonForm((p) => ({
                                ...p,
                                quiz_passing_score: Number(e.target.value),
                              }))
                            }
                            className="rounded-xl border-2 border-[var(--border-light)]"
                          />
                        </div>
                      )}
                    </div>

                    {/* Quiz sorulari inline — interactive tipte */}
                    {lessonForm.lesson_type === 'interactive' && (
                      <div className="mt-4">
                        <p className="text-xs font-bold text-[var(--text-muted)] mb-2">
                          {t('admin.courseManager.section_quiz')}
                        </p>
                        <QuizEditor
                          questions={lessonForm.quiz_questions}
                          onChange={(q) => setLessonForm((p) => ({ ...p, quiz_questions: q }))}
                          t={t}
                        />
                      </div>
                    )}

                    {/* Faz 4e — structured content editors for new lesson types */}
                    {CONTENT_EDITORS[lessonForm.lesson_type] && (() => {
                      const ContentEditor = CONTENT_EDITORS[lessonForm.lesson_type];
                      const lt = LESSON_TYPES.find((x) => x.id === lessonForm.lesson_type);
                      return (
                        <div className="mt-4">
                          <p className="text-xs font-bold text-[var(--text-muted)] mb-2">
                            {lang === 'tr' ? lt?.label_tr : lt?.label_en} — {t('lessonEditor.common.contentSection')}
                          </p>
                          <ContentEditor
                            value={lessonForm.lesson_content || {}}
                            onChange={(newContent) =>
                              setLessonForm((p) => ({ ...p, lesson_content: newContent }))
                            }
                          />
                        </div>
                      );
                    })()}

                    <div className="flex gap-2 justify-end mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setLessonFormOpen(false); setEditingLesson(null); }}
                        className="rounded-xl border-2 font-bold"
                      >
                        {t('admin.courseManager.module_cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveLesson}
                        disabled={createLessonMut.isPending || updateLessonMut.isPending}
                        className="btn-primary rounded-xl font-bold"
                        data-testid="save-lesson-btn"
                      >
                        <Save size={14} className="mr-1" />
                        {t('admin.courseManager.lesson_save')}
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Ders listesi */}
                {lessons.length === 0 ? (
                  <EmptyState
                    icon={<VideoIcon size={40} strokeWidth={1.5} />}
                    title={t('admin.courseManager.lesson_empty')}
                    description={t('admin.courseManager.lesson_empty_desc')}
                  />
                ) : (
                  <div className="space-y-1.5">
                    {[...lessons]
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((l) => {
                        const lt = LESSON_TYPES.find((x) => x.id === l.lesson_type);
                        const Icon = lt?.icon || BookOpen;
                        return (
                          <motion.div
                            key={l.lesson_id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-[var(--border-light)] group hover:border-[var(--brand-primary)]/40 transition-colors"
                          >
                            <span className="text-xs font-bold text-[var(--text-muted)] w-5 text-center">
                              {l.order}
                            </span>
                            <Icon size={16} className="text-[var(--text-muted)] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm truncate text-[var(--text-main)]">
                                {lang === 'tr' ? (l.title_tr || l.title) : (l.title || l.title_tr)}
                              </p>
                              {l.module_title && (
                                <p className="text-xs text-[var(--text-muted)]">{l.module_title}</p>
                              )}
                            </div>
                            <StatusBadge
                              variant="neutral"
                              label={lang === 'tr' ? (lt?.label_tr || l.lesson_type) : (lt?.label_en || l.lesson_type)}
                            />
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => {
                                  setEditingLesson(l);
                                  setLessonForm({
                                    title: l.title || '',
                                    title_tr: l.title_tr || l.title || '',
                                    title_en: l.title_en || '',
                                    description: l.description || '',
                                    description_tr: l.description_tr || '',
                                    order: l.order || 1,
                                    lesson_type: l.lesson_type || 'video',
                                    module_id: l.module_id || '',
                                    module_title: l.module_title || '',
                                    vimeo_url: l.vimeo_url || '',
                                    content: l.content || '',
                                    content_tr: l.content_tr || '',
                                    duration_minutes: l.duration_minutes || 0,
                                    pdf_file_id: l.pdf_file_id || '',
                                    pdf_filename: l.pdf_filename || '',
                                    pdfs: l.pdfs || [],
                                    materials: l.materials || [],
                                    materials_required: l.materials_required || false,
                                    video_min_watch_percent: l.video_min_watch_percent || 80,
                                    quiz_passing_score: l.quiz_passing_score || 70,
                                    quiz_questions: Array.isArray(l.quiz_questions) ? l.quiz_questions : [],
                                    lesson_content: l.lesson_content || null,
                                  });
                                  setLessonFormOpen(true);
                                }}
                                className="p-1.5 rounded-lg hover:bg-blue-50 hover:text-[var(--brand-primary)] text-[var(--text-muted)] transition-colors"
                                title={t('admin.courseManager.lesson_edit')}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => setDeletingLesson(l)}
                                className="p-1.5 rounded-lg hover:bg-red-50 hover:text-[var(--brand-danger)] text-[var(--text-muted)] transition-colors"
                                title={t('admin.courseManager.lesson_delete')}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                )}
              </TabsContent>

              {/* ──────── TAB: QUIZ ───────────────────────────────────── */}
              <TabsContent value="quiz" className="p-6 mt-0">
                <div className="mb-4">
                  <h3 className="font-display font-bold text-base text-[var(--text-main)]">
                    {t('admin.courseManager.section_quiz')}
                  </h3>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    {t('admin.courseManager.section_quiz_desc')}
                  </p>
                </div>

                {/* Ders seç */}
                <div className="mb-4">
                  <label className="text-xs font-bold text-[var(--text-muted)] mb-1 block">
                    {t('admin.courseManager.lesson_type')}: interactive
                  </label>
                  <select
                    value={quizLesson?.lesson_id || ''}
                    onChange={(e) => {
                      const found = lessons.find(
                        (l) => l.lesson_id === e.target.value && l.lesson_type === 'interactive',
                      );
                      setQuizLesson(found || null);
                    }}
                    className="w-full max-w-sm px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
                  >
                    <option value="">— {t('admin.courseManager.quiz_select_lesson')} —</option>
                    {lessons
                      .filter((l) => l.lesson_type === 'interactive')
                      .map((l) => (
                        <option key={l.lesson_id} value={l.lesson_id}>
                          {lang === 'tr' ? (l.title_tr || l.title) : (l.title || l.title_tr)}
                        </option>
                      ))}
                  </select>
                </div>

                {!quizLesson ? (
                  <EmptyState
                    icon={<Zap size={36} className="text-amber-400" />}
                    title={t('admin.courseManager.quiz_empty')}
                    description={t('admin.courseManager.quiz_select_lesson')}
                  />
                ) : (
                  <div className="space-y-4">
                    <QuizEditor
                      questions={quizLesson.quiz_questions || []}
                      onChange={async (newQuestions) => {
                        setQuizLesson((q) => ({ ...q, quiz_questions: newQuestions }));
                        try {
                          await updateLessonMut.mutateAsync({
                            lessonId: quizLesson.lesson_id,
                            data: { ...quizLesson, quiz_questions: newQuestions },
                          });
                        } catch (err) {
                          toast.error(getApiErrorMessage(err, lang));
                        }
                      }}
                      t={t}
                    />
                  </div>
                )}
              </TabsContent>

              {/* ──────── TAB: SERTİFİKA ─────────────────────────────── */}
              <TabsContent value="certificate" className="p-6 mt-0">
                <FormSection
                  title={t('admin.courseManager.section_cert')}
                  columns={2}
                  actions={
                    <Button
                      size="sm"
                      onClick={saveCourse}
                      disabled={updateCourseMut.isPending}
                      className="btn-primary rounded-xl font-bold text-xs"
                    >
                      <Save size={13} className="mr-1" /> {t('admin.courseManager.btn_save')}
                    </Button>
                  }
                >
                  <FormField label={t('admin.courseManager.cert_title')}>
                    <Input
                      value={courseForm.certificate_info?.title || ''}
                      onChange={(e) =>
                        setCourseForm((p) => ({
                          ...p,
                          certificate_info: {
                            ...(p.certificate_info || {}),
                            title: e.target.value,
                          },
                        }))
                      }
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                  <FormField label={t('admin.courseManager.cert_subtitle')}>
                    <Input
                      value={courseForm.certificate_info?.subtitle || ''}
                      onChange={(e) =>
                        setCourseForm((p) => ({
                          ...p,
                          certificate_info: {
                            ...(p.certificate_info || {}),
                            subtitle: e.target.value,
                          },
                        }))
                      }
                      className="rounded-xl border-2 border-[var(--border-light)]"
                    />
                  </FormField>
                </FormSection>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ── Module sil onay ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deletingModule}
        onOpenChange={(o) => !o && setDeletingModule(null)}
        title={t('admin.courseManager.module_delete_title')}
        description={t('admin.courseManager.module_delete_desc')}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleDeleteModule}
        loading={deleteModuleMut.isPending}
      />

      {/* ── Ders sil onay ───────────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deletingLesson}
        onOpenChange={(o) => !o && setDeletingLesson(null)}
        title={t('admin.courseManager.lesson_delete_title')}
        description={t('admin.courseManager.lesson_delete_desc')}
        variant="danger"
        confirmLabel={t('admin.btn_delete')}
        onConfirm={handleDeleteLesson}
        loading={deleteLessonMut.isPending}
      />
    </AdminLayout>
  );
}
